import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { UserProvider } from './context/UserContext';
import { useAuth } from './hooks/useAuth';

// Layouts
import MainLayout from './layout/MainLayout';
import UserLayout from './layout/UserLayout';

// Public Pages
import Home from './pages/public/Home';
import About from './pages/public/About';
import SystemFlow from './pages/public/SystemFlow';
import WhoWeServe from './pages/public/WhoWeServe';
import Insurance from './pages/public/Insurance';
import OurApproach from './pages/public/OurApproach';
import YatharthaEnterpriseSystem from './pages/public/YatharthaEnterpriseSystem';
import Commitment from './pages/public/Commitment';
import FinancialDiagnosisPage from './pages/diagnosis/FinancialDiagnosisPage';
import ReportLandingPage from './pages/diagnosis/ReportLandingPage';
import ReportPrintView from './pages/diagnosis/ReportPrintView';
import RoadmapPage from './pages/diagnosis/RoadmapPage';
import Contact from './pages/public/Contact';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import TermsConditions from './pages/public/TermsConditions';
import RefundCancellation from './pages/public/RefundCancellation';

// Auth Pages
import AuthPage from './pages/Auth/AuthPage';
import AdminLogin from './pages/Auth/AdminLogin';
import ForgotPassword from './pages/Auth/ForgotPassword';

// User Pages
import UserDashboard from './pages/user/Dashboard';
// Import other user pages as they're created

// Admin Pages (V2)
import AdminDashboard from './pages/admin/Dashboard';
import DiagnosisReports from './pages/admin/DiagnosisReports';
import DiagnosisReportDetail from './pages/admin/DiagnosisReportDetail';
import DetailedReports from './pages/admin/DetailedReports';
import TargetAchievement from './pages/admin/TargetAchievement';
import DemoDiagnosis from './pages/admin/DemoDiagnosis';
import IncompleteReports from './pages/admin/IncompleteReports';
import Leads from './pages/admin/Leads/List';
import Settings from './pages/admin/Settings/Roles';
import RoadmapSettings from './pages/admin/Planning/RoadmapSettings';
import FranchiseAdminRegistration from './pages/admin/Planning/FranchiseAdminRegistration';
import FranchiseAdminList from './pages/admin/Planning/FranchiseAdminList';
import FranchisePayoutRequests from './pages/admin/Planning/FranchisePayoutRequests';
import AdminLayout from './components/admin/AdminLayout';
import ComingSoon from './pages/admin/ComingSoon';

// Styles
import './styles/theme.css';
import './styles/global.css';
import './styles/public.css';
import './styles/user.css';
import './styles/contact.css';
import './styles/admin-dashboard.css';
import './styles/auth.css';

// Franchise Pages
import FranchiseDashboard from './pages/franchise/Dashboard';
import FranchiseLayout from './components/franchise/FranchiseLayout';

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false, requireFranchiseAdmin = false }) => {
    const { isAuthenticated, isAdmin, isFranchiseAdmin, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return <div className="loading-screen"><div className="spinner"></div></div>;
    }

    if (!isAuthenticated) {
        if (location.pathname.startsWith('/admin') && location.pathname !== '/admin/login') {
            return <Navigate to="/admin/login" replace />;
        }
        if (location.pathname !== '/login' && location.pathname !== '/register' && !location.pathname.startsWith('/admin')) {
            return <Navigate to="/login" replace />;
        }
        return children;
    }

    if (requireAdmin && !isAdmin) {
        return <Navigate to="/admin/login" replace />;
    }

    if (requireFranchiseAdmin && !isFranchiseAdmin) {
        return <Navigate to="/admin/login" replace />;
    }

    return children;
};

function App() {
    return (
        <AuthProvider>
            <UserProvider>
                <Routes>
                    {/* Admin Login - Separated from protected block to avoid loops */}
                    <Route path="/admin/login" element={<AdminLogin />} />

                    {/* Protected Admin Routes */}
                    <Route 
                        path="/admin/*" 
                        element={
                            <ProtectedRoute requireAdmin={true}>
                                <AdminLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<Navigate to="dashboard" replace />} />
                        <Route path="dashboard" element={<AdminDashboard />} />

                        {/* Diagnosis Section */}
                        <Route path="diagnosis/reports" element={<DiagnosisReports />} />
                        <Route path="diagnosis/reports/:reportId" element={<DiagnosisReportDetail />} />
                        <Route path="diagnosis/targets" element={<TargetAchievement />} />
                        <Route path="diagnosis/demo" element={<DemoDiagnosis />} />
                        <Route path="diagnosis/incomplete" element={<IncompleteReports />} />

                        {/* Planning Section */}
                        <Route path="planning/roadmap" element={<RoadmapSettings />} />
                        <Route path="planning/franchise-admin" element={<FranchiseAdminRegistration />} />
                        <Route path="planning/franchise-admin-list" element={<FranchiseAdminList />} />
                        <Route path="planning/payout-requests" element={<FranchisePayoutRequests />} />
                        <Route path="planning/handson" element={<ComingSoon title="Hands-on Support" />} />
                        <Route path="planning/coaching" element={<ComingSoon title="Coaching & Training" />} />

                        {/* Reports & Payments */}
                        <Route path="reports" element={<DetailedReports />} />
                        <Route path="payments" element={<ComingSoon title="Payment Management" />} />
                        <Route path="leads" element={<Leads />} />

                        <Route path="settings" element={<Settings />} />
                    </Route>

                    {/* Protected Franchise Routes */}
                    <Route 
                        path="/franchise/*" 
                        element={
                            <ProtectedRoute requireFranchiseAdmin={true}>
                                <FranchiseLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<Navigate to="dashboard" replace />} />
                        <Route path="dashboard" element={<FranchiseDashboard />} />
                    </Route>

                    {/* Public Routes */}
                    <Route path="/" element={<MainLayout />}>
                        <Route index element={<Home />} />
                        <Route path="about" element={<About />} />
                        <Route path="system-flow" element={<SystemFlow />} />
                        <Route path="who-we-serve" element={<WhoWeServe />} />
                        <Route path="insurance" element={<Insurance />} />
                        <Route path="our-approach" element={<OurApproach />} />
                        <Route path="yathartha-enterprise-system" element={<YatharthaEnterpriseSystem />} />
                        <Route path="commitment" element={<Commitment />} />
                        <Route path="begin-with-clarity" element={<FinancialDiagnosisPage />} />
                        <Route path="diagnosis-report/:reportId" element={<ReportLandingPage />} />
                        <Route path="report/print/:reportId" element={<ReportPrintView />} />
                        <Route path="roadmap-form/:reportId" element={<RoadmapPage />} />
                        <Route path="contact" element={<Contact />} />
                        <Route path="privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="refund-cancellation" element={<RefundCancellation />} />
                        <Route path="terms-conditions" element={<TermsConditions />} />
                    </Route>

                    {/* Auth Routes */}
                    <Route path="/login" element={<AuthPage />} />
                    <Route path="/register" element={<AuthPage />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />

                    {/* 404 handling */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </UserProvider>
        </AuthProvider>
    );
}

export default App;
